// ===========================================
// COMPONENTE: CrearEventoDialog
// ===========================================
// Dialog for creating a new calendar event

import { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AlertTriangle, Loader2, Video, X } from 'lucide-react'
import { format, addDays, parseISO } from 'date-fns'
import {
  COLORES_CALENDARIO,
  type TipoEvento,
  type EventoCalendarioInsert,
  type EventoOcupado,
} from '@/core/types/calendario'
import {
  useCrearEventoForm,
  VALORES_INICIALES,
  getFieldError,
  emailInvitadoSchema,
  armarRangoEvento,
  tieneHorarioConsultable,
  fechaFinSugerida,
  horaFinSugerida,
  type CrearEventoFormValues,
} from './forms'
import { construirRrule } from '@/core/lib/recurrencia-eventos'
import { useVerificarDisponibilidad } from '@/core/hooks/useCalendario'
import { horaArgentinaDesdeGoogle } from '@/lib/fechaEvento'
import { useStore } from '@tanstack/react-form'
import { FormDialogContent } from '@/components/ui/form-dialog-content'

interface CrearEventoDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCrear: (evento: EventoCalendarioInsert) => Promise<void>
  fechaInicial?: Date | null
}

export function CrearEventoDialog({
  open,
  onOpenChange,
  onCrear,
  fechaInicial,
}: CrearEventoDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  // ORR-227: borrador del email que se está tipeando, antes de sumarlo a la lista
  const [emailBorrador, setEmailBorrador] = useState('')
  const [errorEmail, setErrorEmail] = useState<string | null>(null)

  // ORR-301 · null = todavía no se consultó la agenda; [] = se consultó y está libre.
  // La distinción es la que decide si este submit chequea o ya viene confirmado.
  const [conflictos, setConflictos] = useState<EventoOcupado[] | null>(null)
  const verificarDisponibilidad = useVerificarDisponibilidad()
  const hayConflictos = (conflictos?.length ?? 0) > 0

  // ORR-389 · una vez que el usuario toca el fin a mano, dejamos de autocompletarlo: pisar
  // lo que alguien ya escribió molesta más de lo que ayuda. Es un ref (no state): solo se lee
  // dentro de handlers, nunca en el render, así que no necesita disparar un re-render.
  const fechaFinTocada = useRef(false)
  const horaFinTocada = useRef(false)
  const resetAutocompletarFin = () => {
    fechaFinTocada.current = false
    horaFinTocada.current = false
  }

  // Cambiar el horario invalida el aviso: confirmar un choque no puede arrastrarse a
  // un horario distinto, que es donde el aviso dejaría de decir la verdad.
  const cambiarHorario = (
    handler: (valor: string) => void,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setConflictos(null)
    handler(e.target.value)
  }

  const { form } = useCrearEventoForm({
    onSubmit: async (value: CrearEventoFormValues) => {
      setIsSubmitting(true)
      try {
        const { fechaInicio, fechaFin } = armarRangoEvento(value)

        // ORR-301 · primera pasada: preguntarle a la agenda del estudio. Si algo se pisa,
        // se muestra y se corta acá sin guardar nada. El segundo submit ya viene con la
        // decisión tomada — `conflictos` deja de ser null y esto no se vuelve a ejecutar.
        if (conflictos === null && tieneHorarioConsultable(value) && fechaFin) {
          try {
            const ocupado = await verificarDisponibilidad.mutateAsync({
              fechaInicio,
              fechaFin,
            })
            setConflictos(ocupado)
            if (ocupado.length > 0) return
          } catch (error) {
            // Que no se pueda leer la agenda no puede impedir agendar: el aviso es una
            // ayuda, no una validación. Se sigue de largo y el alta queda como antes.
            console.error('[calendario] No se pudo consultar la agenda del estudio:', error)
            setConflictos([])
          }
        }

        const reglaRecurrencia = construirRrule(value.repeticion ?? 'ninguna', value.repetir_hasta)

        const evento: EventoCalendarioInsert = {
          titulo: value.titulo,
          descripcion: value.descripcion ?? null,
          tipo: value.tipo as TipoEvento,
          fecha_inicio: fechaInicio,
          fecha_fin: fechaFin,
          todo_el_dia: value.todo_el_dia,
          estado: value.estado ?? 'pendiente',
          color: value.color ?? null,
          ubicacion: value.ubicacion ?? null,
          notas: value.notas ?? null,
          participantes: value.participantes?.length ? value.participantes : null,
          // ORR-383: `recurrente` se deriva de la regla en vez de guardarse aparte. Son dos
          // columnas que describen lo mismo, y mantenerlas a mano abre la puerta a la fila
          // marcada como serie que no tiene regla que expandir.
          recurrente: reglaRecurrencia !== null,
          regla_recurrencia: reglaRecurrencia,
        }

        await onCrear(evento)
        form.reset()
        setConflictos(null)
        resetAutocompletarFin()
        onOpenChange(false)
      } catch (error) {
        console.error('Error al crear evento:', error)
      } finally {
        setIsSubmitting(false)
      }
    },
    defaultValues: {
      fecha_inicio: fechaInicial ? format(fechaInicial, 'yyyy-MM-dd') : '',
    },
  })

  // Precargar la fecha que el usuario clickeó en el calendario.
  // Va por `reset` y no por `setFieldValue` a propósito: precargar define el punto de partida
  // del formulario, no lo edita. Con `setFieldValue` el form nace "sucio" y la guarda de
  // ORR-392 pediría confirmación para descartar algo que el usuario nunca escribió.
  useEffect(() => {
    if (fechaInicial && open) {
      // `keepDefaultValues` conserva los valores por defecto del formulario: sin eso, `reset`
      // los reemplaza por los del evento y el siguiente re-render los detecta distintos de los
      // que declara el hook, así que vuelve a aplicar los iniciales y el formulario queda vacío.
      form.reset(
        { ...VALORES_INICIALES, fecha_inicio: format(fechaInicial, 'yyyy-MM-dd') },
        { keepDefaultValues: true },
      )
    }
  }, [fechaInicial, open])

  // Reset form on close
  const isDirty = useStore(form.store, (s) => s.isDirty)

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset()
      setEmailBorrador('')
      setErrorEmail(null)
      setConflictos(null)
      resetAutocompletarFin()
    }
    onOpenChange(newOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <FormDialogContent
        isDirty={isDirty}
        onDiscard={() => handleOpenChange(false)} className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="h-5 w-5 text-primary" />
            Nueva reunión
          </DialogTitle>
          <DialogDescription>
            Se crea con sala de Google Meet y queda en el calendario del estudio
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
          className="space-y-4"
        >
          {/* Título */}
          <form.Field name="titulo">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Título *</Label>
                <Input
                  id={field.name}
                  placeholder="Ej: Reunión con cliente"
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {getFieldError(field.state.meta.errors)}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          {/* Color */}
          <form.Field name="color">
            {(field) => (
              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex gap-1.5 pt-1">
                  {COLORES_CALENDARIO.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`w-6 h-6 rounded-full border-2 transition-all ${
                        field.state.value === color
                          ? 'border-foreground scale-110'
                          : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() =>
                        field.handleChange(
                          field.state.value === color ? null : color
                        )
                      }
                    />
                  ))}
                </div>
              </div>
            )}
          </form.Field>

          {/* Todo el día toggle */}
          <form.Field name="todo_el_dia">
            {(field) => (
              <div className="flex items-center gap-3">
                <Switch
                  id="todo_el_dia"
                  checked={field.state.value}
                  onCheckedChange={(v) => {
                    setConflictos(null)
                    field.handleChange(v)
                  }}
                />
                <Label htmlFor="todo_el_dia">Todo el día</Label>
              </div>
            )}
          </form.Field>

          {/* Fechas */}
          <form.Subscribe selector={(state) => state.values.todo_el_dia}>
            {(todoElDia) => (
              <div className="grid grid-cols-2 gap-4">
                <form.Field name="fecha_inicio">
                  {(field) => (
                    <div className="space-y-2">
                      <Label htmlFor={field.name}>
                        Fecha inicio *
                      </Label>
                      <Input
                        id={field.name}
                        type="date"
                        value={field.state.value || ''}
                        onChange={(e) => {
                          cambiarHorario(field.handleChange, e)
                          // ORR-389: solo con "todo el día" desmarcado — un evento de día
                          // completo puede abarcar varios días y esto no debe tocarlo.
                          if (!todoElDia && e.target.value) {
                            const sugerida = fechaFinSugerida(e.target.value, fechaFinTocada.current)
                            if (sugerida !== null) form.setFieldValue('fecha_fin', sugerida)
                          }
                        }}
                        onBlur={field.handleBlur}
                      />
                      {field.state.meta.errors.length > 0 && (
                        <p className="text-sm text-destructive">
                          {getFieldError(field.state.meta.errors)}
                        </p>
                      )}
                    </div>
                  )}
                </form.Field>

                {!todoElDia && (
                  <form.Field name="hora_inicio">
                    {(field) => (
                      <div className="space-y-2">
                        <Label htmlFor={field.name}>Hora inicio</Label>
                        <Input
                          id={field.name}
                          type="time"
                          value={field.state.value || ''}
                          onChange={(e) => {
                            cambiarHorario(field.handleChange, e)
                            if (!e.target.value) return
                            // ORR-389: inicio + 30 minutos. Si cruza la medianoche, la fecha
                            // de fin corre un día — salvo que el usuario ya la haya editado.
                            const sugerida = horaFinSugerida(e.target.value, horaFinTocada.current)
                            if (!sugerida) return
                            form.setFieldValue('hora_fin', sugerida.hora)
                            if (sugerida.cruzaMedianoche && !fechaFinTocada.current) {
                              const fechaInicioActual = form.getFieldValue('fecha_inicio')
                              if (fechaInicioActual) {
                                form.setFieldValue(
                                  'fecha_fin',
                                  format(addDays(parseISO(fechaInicioActual), 1), 'yyyy-MM-dd'),
                                )
                              }
                            }
                          }}
                        />
                      </div>
                    )}
                  </form.Field>
                )}

                <form.Field name="fecha_fin">
                  {(field) => (
                    <div className="space-y-2">
                      <Label htmlFor={field.name}>Fecha fin</Label>
                      <Input
                        id={field.name}
                        type="date"
                        value={field.state.value || ''}
                        onChange={(e) => {
                          cambiarHorario(field.handleChange, e)
                          fechaFinTocada.current = true
                        }}
                      />
                    </div>
                  )}
                </form.Field>

                {!todoElDia && (
                  <form.Field name="hora_fin">
                    {(field) => (
                      <div className="space-y-2">
                        <Label htmlFor={field.name}>Hora fin</Label>
                        <Input
                          id={field.name}
                          type="time"
                          value={field.state.value || ''}
                          onChange={(e) => {
                            cambiarHorario(field.handleChange, e)
                            horaFinTocada.current = true
                          }}
                        />
                      </div>
                    )}
                  </form.Field>
                )}
              </div>
            )}
          </form.Subscribe>

          {/* Repetición (ORR-383) */}
          <div className="grid grid-cols-2 gap-4">
            <form.Field name="repeticion">
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor="repeticion">Repetir</Label>
                  <Select
                    value={field.state.value ?? 'ninguna'}
                    onValueChange={(v) =>
                      field.handleChange(v as CrearEventoFormValues['repeticion'])
                    }
                  >
                    <SelectTrigger id="repeticion">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ninguna">No se repite</SelectItem>
                      <SelectItem value="semanal">Cada semana</SelectItem>
                      <SelectItem value="mensual">Cada mes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <form.Subscribe selector={(state) => state.values.repeticion}>
              {(repeticion) =>
                !repeticion || repeticion === 'ninguna' ? null : (
                  <form.Field name="repetir_hasta">
                    {(field) => (
                      <div className="space-y-2">
                        <Label htmlFor={field.name}>Hasta (opcional)</Label>
                        <Input
                          id={field.name}
                          type="date"
                          value={field.state.value || ''}
                          onChange={(e) => field.handleChange(e.target.value || null)}
                        />
                      </div>
                    )}
                  </form.Field>
                )
              }
            </form.Subscribe>
          </div>

          <form.Subscribe selector={(state) => state.values.repeticion}>
            {(repeticion) =>
              !repeticion || repeticion === 'ninguna' ? null : (
                <p className="text-muted-foreground text-xs">
                  Se repite {repeticion === 'semanal' ? 'todas las semanas' : 'todos los meses'} a
                  partir de la fecha de inicio. La invitación sale una sola vez, por toda la serie.
                </p>
              )
            }
          </form.Subscribe>

          {/* Ubicación */}
          <form.Field name="ubicacion">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Ubicación</Label>
                <Input
                  id={field.name}
                  placeholder="Ej: Oficina, Zoom, etc."
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
              </div>
            )}
          </form.Field>

          {/* Invitados (ORR-227) */}
          <form.Field name="participantes">
            {(field) => {
              const invitados = field.state.value ?? []

              const agregarInvitado = () => {
                const parsed = emailInvitadoSchema.safeParse(emailBorrador)
                if (!parsed.success) {
                  setErrorEmail('Email inválido')
                  return
                }
                if (invitados.includes(parsed.data)) {
                  setErrorEmail('Ese email ya está invitado')
                  return
                }
                field.handleChange([...invitados, parsed.data])
                setEmailBorrador('')
                setErrorEmail(null)
              }

              return (
                <div className="space-y-2">
                  <Label htmlFor="invitado-email">Invitados</Label>
                  <p className="text-xs text-muted-foreground">
                    El estudio ya es el organizador: agregá acá solo a clientes o colaboradores.
                  </p>
                  <div className="flex gap-2">
                    <Input
                      id="invitado-email"
                      type="email"
                      placeholder="email@ejemplo.com"
                      value={emailBorrador}
                      onChange={(e) => {
                        setEmailBorrador(e.target.value)
                        setErrorEmail(null)
                      }}
                      onKeyDown={(e) => {
                        // Enter agrega el invitado en vez de mandar el formulario
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          agregarInvitado()
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={agregarInvitado}
                      disabled={!emailBorrador.trim()}
                    >
                      Agregar
                    </Button>
                  </div>
                  {errorEmail && (
                    <p className="text-sm text-destructive">{errorEmail}</p>
                  )}
                  {invitados.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {invitados.map((email) => (
                        <Badge key={email} variant="secondary" className="gap-1">
                          {email}
                          <button
                            type="button"
                            aria-label={`Quitar a ${email}`}
                            className="hover:text-destructive"
                            onClick={() =>
                              field.handleChange(invitados.filter((e) => e !== email))
                            }
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )
            }}
          </form.Field>

          {/* Notas */}
          <form.Field name="notas">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Notas</Label>
                <Textarea
                  id={field.name}
                  placeholder="Notas adicionales..."
                  className="resize-none"
                  rows={2}
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
              </div>
            )}
          </form.Field>

          {/* ORR-301 · lo que ya está en la agenda del estudio a esa hora */}
          {hayConflictos && (
            <div className="flex items-start gap-2 rounded-lg bg-muted/50 p-3 text-sm">
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
              <div className="flex-1 space-y-1.5">
                <p className="font-medium">
                  {conflictos!.length === 1
                    ? 'Ya hay algo agendado a esa hora'
                    : `Ya hay ${conflictos!.length} eventos agendados a esa hora`}
                </p>
                <ul className="space-y-0.5 text-muted-foreground">
                  {conflictos!.map((ocupado) => (
                    <li key={`${ocupado.inicio}-${ocupado.titulo}`}>
                      {horaArgentinaDesdeGoogle(ocupado.inicio)} a{' '}
                      {horaArgentinaDesdeGoogle(ocupado.fin)} ·{' '}
                      {ocupado.titulo}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground">
                  Si la superposición es a propósito, podés agendar igual.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {hayConflictos ? 'Agendar igual' : 'Crear reunión'}
            </Button>
          </DialogFooter>
        </form>
      </FormDialogContent>
    </Dialog>
  )
}
